import { useState } from "react";
import { useTranslation } from "react-i18next";
import FormBlock from "../../../Components/Inputs/FormBlock";
import TrailerOutSearchSelect from "../../../Components/Inputs/TrailerOutSearch";
import { useApiMutation } from "../../../Hooks/useApiMutation";
import instance from "../../../Interceptor/api_instance";
import { ActionResponse } from "../../../Components/Modals/ActionResponses";
import { Spinner } from "../../../Components/LoadingAnimations/LoadingAnimations";

/**
 * EditSeal – modal form to update an existing seal's number and trailer assignment.
 * @param {Object}   seal      - the seal record being edited.
 * @param {Function} close     - closes the modal.
 * @param {Function} onSuccess - receives the updated seal object so the parent can patch its list in-place.
 */
const EditSeal = ({ seal, close, onSuccess }) => {
  const { t } = useTranslation();

  // Initialise form state from the existing seal; map nested check_in to a select-friendly shape
  const [formData, setFormData] = useState({
    seal_number: seal.seal_number,
    trailer: seal.check_in
      ? {
          label: seal.check_in.trailer.trailer_number,
          value: seal.check_in.trailer.id,
        }
      : null,
  });

  // Mutation hook: handles loading / error state for the PATCH request
  const {
    mutate: updateSeal,
    isLoading,
    error,
    setError,
  } = useApiMutation(async (payload, { signal }) => {
    const response = await instance.patch(`safety/seals/${seal.id}/`, payload, {
      signal,
    });
    return response;
  });

  // Submit handler: sends the flattened payload, then passes the API response to the parent
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      seal_number: formData.seal_number,
      trailer: formData.trailer ? formData.trailer.value : null,
    };

    const response = await updateSeal(payload);
    // response is undefined when the request was aborted or caught an error
    if (response?.status === 200) {
      onSuccess(response.data);
      close();
    }
  };

  return (
    <div className="page-mask page-mask-animation">
      {/* Display API error as a dismissible banner */}
      <div className="edit-modal">
        {error && (
          <p
            className="error"
            style={{ maxWidth: "200px", flexWrap: "wrap", textAlign: "center", alignSelf: "center" }}
          >
            {error}
          </p>
        )}
        <form onSubmit={handleSubmit}>
          <FormBlock
            label={t("yard-tables.table-columns.seal")}
            name={"seal_number"}
            update={(e) =>
              setFormData((prev) => ({
                ...prev,
                seal_number: e.target.value,
              }))
            }
            isRequired={true}
            placeholder={t("yard-tables.table-columns.seal")}
            value={formData.seal_number}
          />

          {/* Trailer search select – allows reassigning the seal to a different check-in trailer */}
          <div className="margin-bottom-24 form-block">
            <label className="">{t("yard-tables.table-columns.trailer")}</label>
            <div className="input-cont">
              <TrailerOutSearchSelect
                onSelect={(option) =>
                  setFormData((prev) => ({
                    ...prev,
                    trailer: option,
                  }))
                }
                selectedTrailer={formData.trailer}
                required={false}
              />
            </div>
          </div>

          <div className="full-cont">
            {isLoading ? (
              <Spinner />
            ) : (
              <>
                <button className="change-pwd-btn all-btn">
                  {t("buttons.submit")}
                </button>
                <button
                  className="cancel-btn all-btn"
                  type="button"
                  onClick={close}
                >
                  {t("buttons.cancel")}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSeal;
